import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle2, Phone, Mail, Building2, Home, Thermometer, Hammer, Layers, Beef, Quote, Sparkles, ShieldCheck, Clock, Award, Zap, Flame, Star, Heart, Search, ClipboardCheck, ThumbsUp, ZoomIn } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { SERVICES, COMPANY, REFERENCES } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { SERVICE_TRANSLATIONS } from '../i18n/translations';
import { Reveal } from '../hooks/useReveal';
import Lightbox from '../components/Lightbox';
import AnimatedCounter from '../components/AnimatedCounter';
import MagneticButton from '../components/MagneticButton';
import SEO, { serviceLd, breadcrumbLd } from '../components/SEO';

const iconMap = { Building2, Home, Thermometer, Hammer, Layers, Beef };

const statsMap = {
  kundenmaurer: [
    { icon: Hammer, value: '20+', label: 'Jahre Erfahrung' },
    { icon: Star, value: '5.0', label: 'Kundenbewertung' },
    { icon: ShieldCheck, value: '100%', label: 'Zuverlässigkeit' },
    { icon: ThumbsUp, value: 'Fair', label: 'Preis-Leistung' },
  ],
  plattenleger: [
    { icon: Layers, value: '500+', label: 'Verlegte m²' },
    { icon: Star, value: '5.0', label: 'Kundenbewertung' },
    { icon: Clock, value: '20+', label: 'Jahre Erfahrung' },
    { icon: Award, value: 'AA+', label: 'Qualität' },
  ],
  abhof: [
    { icon: Beef, value: 'BIO', label: 'Zertifiziert' },
    { icon: Heart, value: '100%', label: 'Schweizer Qualität' },
    { icon: ShieldCheck, value: 'Frei', label: 'Antibiotika' },
    { icon: Award, value: 'Direkt', label: 'Ab Hof' },
  ],
  hardrock: [
    { icon: Sparkles, value: '2000+', label: 'Farbtöne' },
    { icon: ShieldCheck, value: 'R11', label: 'Rutschhemmung' },
    { icon: Award, value: 'GIA', label: 'Innovation Award' },
    { icon: Star, value: '2024', label: 'German Design Award' },
  ],
};

const benefitsMap = {
  kundenmaurer: [
    { icon: Hammer, title: 'Bruchsteinmauern', text: 'Einzigartige Steinmauern für unverwechselbares Ambiente.' },
    { icon: Flame, title: 'Pizzaöfen', text: 'Authentische Pizzaöfen für Ihren Aussenbereich.' },
    { icon: ShieldCheck, title: 'Verputzarbeiten', text: 'Fachgerechter Verputz und Ausbesserungen.' },
    { icon: Heart, title: 'Persönlich', text: 'Direkte Betreuung durch den Chef – Armin selbst.' },
  ],
  plattenleger: [
    { icon: Sparkles, title: 'Naturstein', text: 'Exklusive Natursteine für Wand und Boden.' },
    { icon: Layers, title: 'Vielseitig', text: 'Fliesen, Mosaik, Parkett, Holz, Teppich, Linoleum.' },
    { icon: Home, title: 'Bad & Nassraum', text: 'Spezialisiert auf Feuchträume und Bäder.' },
    { icon: Award, title: 'Präzision', text: 'Millimetergenaue Verlegung mit AA+ Qualität.' },
  ],
  abhof: [
    { icon: Beef, title: 'BIO Rindfleisch', text: 'Schweizer BIO-Qualität aus eigener Produktion.' },
    { icon: Heart, title: 'Familienbetrieb', text: 'Generationsgemeinschaft mit meinem Sohn.' },
    { icon: ShieldCheck, title: 'Antibiotikafrei', text: 'Cholesterinarm und frei von Antibiotika.' },
    { icon: Sparkles, title: 'Frisch ab Hof', text: 'Direkt vom Metzger in der Region – zu Ihnen.' },
  ],
  hardrock: [
    { icon: Sparkles, title: 'HardRock ULTRA 2K', text: 'Zweikomponentiger MicroTec Putz, epoxidharzfrei, ca. 2 h Offenzeit.' },
    { icon: Layers, title: 'HardRock PRO', text: 'Einkomponentig verarbeitungsfertig – schnell und einfach anzuwenden.' },
    { icon: ShieldCheck, title: 'Wasserdicht & R11', text: 'Ideal für Nassräume, mit Rutschhemmung R11 zertifiziert.' },
    { icon: Award, title: 'Prämiert', text: 'German Innovation Award & German Design Award 2024.' },
  ],
};

const processSteps = [
  { num: '01', icon: Phone, title: 'Anruf & Termin', text: 'Sie rufen an, wir besprechen Ihr Projekt.' },
  { num: '02', icon: Search, title: 'Vor-Ort-Besichtigung', text: 'Kostenlose Begehung & individuelle Beratung.' },
  { num: '03', icon: ClipboardCheck, title: 'Faires Angebot', text: 'Transparente Offerte – ohne versteckte Kosten.' },
  { num: '04', icon: Hammer, title: 'Termintreue Ausführung', text: 'Pünktlich, sauber und in höchster Qualität.' },
];

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const service = SERVICES.find((s) => s.slug === slug);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (!service) navigate('/dienstleistungen', { replace: true });
    window.scrollTo(0, 0);
  }, [service, navigate, slug]);

  if (!service) return null;

  const Icon = iconMap[service.icon] || Building2;
  const st = SERVICE_TRANSLATIONS[service.id]?.[lang] || {};
  const stats = statsMap[service.id] || [];
  const benefits = benefitsMap[service.id] || [];
  const otherServices = SERVICES.filter((s) => s.id !== service.id);
  const categoryRefs = REFERENCES.filter((r) => r.category === service.id);

  return (
    <div className="bg-white">
      <SEO
        title={`${st.title} in Seelisberg · Uri`}
        description={`${st.description?.substring(0, 155)}`}
        path={`/dienstleistungen/${service.slug}`}
        image={service.image}
        lang={lang}
        jsonLd={[
          serviceLd({ name: st.title, description: st.description, image: service.image, slug: service.slug }),
          breadcrumbLd([
            { name: 'Home', path: '/' },
            { name: 'Dienstleistungen', path: '/dienstleistungen' },
            { name: st.title, path: `/dienstleistungen/${service.slug}` },
          ]),
        ]}
      />
      {/* ═══════════ HERO ═══════════ */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden bg-brand-blueDark">
        <div className="absolute inset-0">
          <img src={service.image} alt={st.title} className="w-full h-full object-cover animate-kenburns" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-blueDark via-brand-blueDark/85 to-brand-blueDark/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blueDark/70 to-transparent"></div>
        </div>

        {/* Ambient blobs */}
        <div className="absolute top-24 -right-32 w-[500px] h-[500px] bg-brand-blueLight/20 rounded-full blur-3xl"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-40">
          <Link
            to="/dienstleistungen"
            className="inline-flex items-center gap-2 text-white/70 hover:text-brand-blueSky text-xs uppercase tracking-[0.3em] mb-8 transition-colors group"
            data-testid="service-back-link"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {t('services.backToServices')}
          </Link>

          <Reveal>
            <div className="max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-md bg-white flex items-center justify-center shadow-2xl shadow-brand-blue/40">
                  <Icon className="w-8 h-8 text-brand-blueDark" />
                </div>
                <div className="px-4 py-1.5 bg-brand-blue text-white text-[10px] uppercase tracking-[0.3em] rounded-sm font-bold">
                  {t('nav.services')}
                </div>
              </div>
              <h1 className="font-display text-5xl md:text-7xl text-white leading-[1.02] mb-6 tracking-tight">
                {st.title}
              </h1>
              <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-2xl mb-10">{st.description}</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <MagneticButton>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center justify-center gap-2 bg-white text-brand-blueDark hover:bg-brand-blueSky px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm shadow-xl transition-colors btn-pulse-blue"
                    data-testid="service-cta-request"
                  >
                    {t('cta.request')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </MagneticButton>
                <a
                  href={`tel:${COMPANY.phoneInternational}`}
                  className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors"
                  data-testid="service-cta-phone"
                >
                  <Phone className="w-4 h-4" /> {COMPANY.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ STATS BAR ═══════════ */}
      <section className="relative -mt-16 z-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto bg-white rounded-md shadow-2xl shadow-brand-blueDark/20 border border-brand-blueMist p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => {
              const SIcon = s.icon;
              return (
                <Reveal key={i} delay={i * 80}>
                  <div className="text-center md:border-r last:border-r-0 border-brand-blueMist">
                    <SIcon className="w-6 h-6 mx-auto mb-2 text-brand-blue" />
                    <div className="font-display text-3xl md:text-4xl font-bold text-brand-blueDark leading-none mb-1">
                      <AnimatedCounter value={s.value} />
                    </div>
                    <div className="text-xs uppercase tracking-widest text-brand-ink/60 font-bold">{s.label}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ BENEFITS ═══════════ */}
      <section className="py-28 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">Ihre Vorteile</div>
              <h2 className="font-display text-4xl md:text-5xl text-brand-blueDark leading-tight tracking-tight">
                Darum lohnt sich <span className="italic text-brand-blue">{st.short}</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => {
              const BIcon = b.icon;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="group relative bg-brand-beige border border-brand-blueMist hover:border-brand-blue rounded-md p-7 h-full lift-on-hover overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-24 h-24 bg-brand-blueMist/60 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="relative">
                      <div className="w-14 h-14 rounded-md bg-brand-blue text-white flex items-center justify-center mb-5 shadow-lg shadow-brand-blue/20 group-hover:scale-110 group-hover:-rotate-6 transition-all">
                        <BIcon className="w-7 h-7" />
                      </div>
                      <h3 className="font-display text-xl text-brand-blueDark mb-2">{b.title}</h3>
                      <p className="text-sm text-brand-ink/70 leading-relaxed">{b.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ PROCESS ═══════════ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-brand-beige relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand-blueLight/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">So einfach geht's</div>
              <h2 className="font-display text-4xl md:text-5xl text-brand-blueDark leading-tight tracking-tight">
                In <span className="italic text-brand-blue">4 Schritten</span> zum Erfolg
              </h2>
              <p className="text-brand-ink/70 mt-4">Vom ersten Anruf bis zur fertigen Arbeit – transparent und stressfrei.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative">
            {processSteps.map((p, i) => {
              const PIcon = p.icon;
              return (
                <Reveal key={p.num} delay={i * 120}>
                  <div className="relative bg-white border border-brand-blueMist rounded-md p-7 h-full lift-on-hover group">
                    <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-brand-blueDark text-white flex items-center justify-center font-display text-lg font-bold shadow-xl">
                      {p.num}
                    </div>
                    <div className="w-12 h-12 rounded-md bg-brand-blueMist text-brand-blueDark flex items-center justify-center mb-5 mt-4 group-hover:bg-brand-blue group-hover:text-white transition-colors">
                      <PIcon className="w-6 h-6" />
                    </div>
                    <h3 className="font-display text-lg text-brand-blueDark mb-2">{p.title}</h3>
                    <p className="text-sm text-brand-ink/70 leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ INCLUDES + STICKY CTA ═══════════ */}
      <section className="py-28 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Reveal>
              <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('services.myPromise').replace('.', '')}</div>
              <h2 className="font-display text-3xl md:text-5xl text-brand-blueDark mb-6 leading-tight tracking-tight">
                Was im Service <span className="italic text-brand-blue">enthalten</span> ist
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {(st.bullets || []).map((b, i) => (
                <Reveal key={i} delay={i * 80}>
                  <div className="flex items-start gap-3 bg-brand-beige border border-brand-blueMist hover:border-brand-blue px-5 py-4 rounded-md transition-colors">
                    <CheckCircle2 className="w-5 h-5 text-brand-blue mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-brand-ink font-medium">{b}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-5">
              <div className="relative bg-brand-blueDark text-white p-7 rounded-md overflow-hidden shadow-2xl shadow-brand-blue/30">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand-blueLight/30 rounded-full blur-2xl"></div>
                <div className="relative">
                  <Quote className="w-8 h-8 text-brand-blueSky/60 mb-4" />
                  <p className="text-base leading-relaxed italic mb-5 font-medium">«Zufriedene Kunden sind unser Antrieb und unsere Motivation.»</p>
                  <div className="text-xs uppercase tracking-widest text-brand-blueSky font-bold">Frachigers Handwerk</div>
                </div>
              </div>

              <div className="bg-brand-beige border border-brand-blueMist p-7 rounded-md">
                <div className="text-brand-blueLight uppercase tracking-widest text-[10px] font-bold mb-3">Jetzt sichern</div>
                <h3 className="font-display text-2xl text-brand-blueDark mb-5">Kostenlose Beratung</h3>
                <a
                  href={`tel:${COMPANY.phoneInternational}`}
                  className="flex items-center gap-3 px-4 py-3 bg-white border border-brand-blueMist rounded-sm hover:border-brand-blue transition-colors mb-3 group"
                  data-testid="sidebar-phone"
                >
                  <Phone className="w-4 h-4 text-brand-blue group-hover:scale-110 transition-transform" />
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-widest text-brand-ink/50">{t('contact.phone')}</div>
                    <div className="text-sm font-bold text-brand-blueDark">{COMPANY.phone}</div>
                  </div>
                </a>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-3 px-4 py-3 bg-white border border-brand-blueMist rounded-sm hover:border-brand-blue transition-colors group"
                  data-testid="sidebar-email"
                >
                  <Mail className="w-4 h-4 text-brand-blue group-hover:scale-110 transition-transform" />
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-widest text-brand-ink/50">{t('common.email')}</div>
                    <div className="text-sm font-bold text-brand-blueDark break-all">{COMPANY.email}</div>
                  </div>
                </a>
              </div>

              <MagneticButton className="block w-full">
                <Link
                  to="/kontakt"
                  className="block w-full text-center bg-brand-blue hover:bg-brand-blueDark text-white px-6 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors shadow-xl shadow-brand-blue/30 btn-pulse-blue"
                  data-testid="sidebar-cta"
                >
                  {t('cta.request')} →
                </Link>
              </MagneticButton>
            </div>
          </aside>
        </div>
      </section>

      {/* ═══════════ RELATED REFERENCES — WITH LIGHTBOX ═══════════ */}
      {categoryRefs.length > 0 && (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-brand-beige">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                  <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('services.exampleProjects')}</div>
                  <h2 className="font-display text-3xl md:text-5xl text-brand-blueDark leading-tight tracking-tight">
                    Realisiert für <span className="italic text-brand-blue">echte Kunden</span>
                  </h2>
                  <p className="text-brand-ink/70 mt-3 text-sm">Klicken Sie auf ein Bild und blättern Sie mit den Pfeiltasten durch die Galerie.</p>
                </div>
                <Link to="/referenzen" className="group inline-flex items-center gap-2 text-brand-blueDark hover:text-brand-blue text-sm uppercase tracking-wider font-bold" data-testid="all-refs-link">
                  {t('services.allReferences')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categoryRefs.slice(0, 8).map((r, i) => (
                <Reveal key={r.id} delay={i * 70}>
                  <button
                    onClick={() => setLightboxIndex(i)}
                    className="group relative block w-full text-left overflow-hidden rounded-md bg-white lift-on-hover"
                    data-testid={`ref-thumb-${r.id}`}
                  >
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.titleKey}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-brand-blueDark/0 group-hover:bg-brand-blueDark/40 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white text-brand-blueDark flex items-center justify-center opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all">
                          <ZoomIn className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                    {/* Solid white caption, always readable */}
                    <div className="p-4 border-t border-brand-blueMist">
                      <div className="text-brand-blueLight text-[10px] uppercase tracking-widest font-bold mb-1">{r.location}</div>
                      <div className="text-brand-blueDark font-display text-sm leading-tight">{r.titleKey}</div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ OTHER SERVICES ═══════════ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="mb-10">
              <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('services.otherServices')}</div>
              <h2 className="font-display text-3xl md:text-5xl text-brand-blueDark tracking-tight">
                Mehr von <span className="italic text-brand-blue">Frachigers</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {otherServices.map((s, i) => {
              const OIcon = iconMap[s.icon] || Building2;
              const ost = SERVICE_TRANSLATIONS[s.id]?.[lang] || {};
              return (
                <Reveal key={s.id} delay={i * 100}>
                  <Link
                    to={`/dienstleistungen/${s.slug}`}
                    className="group relative bg-brand-beige border border-brand-blueMist hover:border-brand-blue rounded-md overflow-hidden flex transition-all lift-on-hover"
                    data-testid={`other-service-${s.slug}`}
                  >
                    <div className="w-1/3 relative overflow-hidden">
                      <img src={s.image} alt={ost.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    </div>
                    <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-md bg-brand-blue text-white flex items-center justify-center">
                          <OIcon className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-brand-blueLight font-bold">{t('nav.services')}</span>
                      </div>
                      <h3 className="font-display text-xl md:text-2xl text-brand-blueDark mb-2 group-hover:text-brand-blue transition-colors">{ost.title}</h3>
                      <span className="inline-flex items-center gap-2 text-sm text-brand-blue font-bold mt-2 uppercase tracking-wider">
                        {t('common.more')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox portal */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            images={categoryRefs}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onIndexChange={setLightboxIndex}
            subtitle={`${st.short} · ${categoryRefs[lightboxIndex]?.location}`}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServiceDetailPage;
